import { useEffect, useState } from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";
// biome-ignore lint/style/noRestrictedImports: legacy suppression
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import { useUser } from "src/contexts/user-context";
import { adminSwitchWorkspace, switchWorkspace } from "src/utils/workspaces";
import { topLevelRoutes } from "./constants";
import { PageSpinner } from "src/components/loading";
import { queryClient } from "src/lib/react-query";
import { useGetWorkspaceEntitlementsQuery, useInitialQuery } from "src/graphql";
import { fetcher } from "src/utils/fetcher";
import { useUpdateSentryTransactionNamesForWorkspaceRoutes } from "./use-sentry-tracking";
import { useSentryQueryParamTags } from "./use-sentry-query-param-tags";

export const loader = async () => {
  // Note: for now we don't want this loader to await on the promise
  // We haven't fully implemented a good UI to use for route loaders,
  // and awaiting here would make some route transition seem slow/frozen in the app.
  // However, we still do want a loader fn so that can be called early in the app
  // initialization cycle, and we can parallelize these two potentially slow requests.
  // prefetchQuery leaves a failure in the query state, where UserProvider reads it, instead of an unhandled rejection.
  void Promise.all([
    queryClient.prefetchQuery({
      meta: { isFastEndpoint: true },
      queryKey: useInitialQuery.getKey({}),
      queryFn: fetcher(useInitialQuery.document, {}),
    }),
    queryClient.prefetchQuery({
      queryKey: useGetWorkspaceEntitlementsQuery.getKey(),
      queryFn: fetcher(useGetWorkspaceEntitlementsQuery.document),
    }),
  ]);
  return null;
};

export const Element = () => {
  const params = useParams<{
    root_workspace_slug: string;
  }>();
  const location = useLocation();
  const { slug, getWorkspaceIdFromSlug } = useUser();

  const rootWorkspaceSlug = params.root_workspace_slug;
  // React Router stores the rest of the params in "*"
  // e.g. /destinations/new/foo -> params = { root_workspace_slug: "destinations", "*": "new/foo" }
  const otherParams = params["*"] ? `/${params["*"]}` : "";

  // Redirect without switchWorkspace. A workspace whose slug matches a page name keeps its own URLs.
  if (
    slug &&
    rootWorkspaceSlug &&
    topLevelRoutes.includes(rootWorkspaceSlug) &&
    !getWorkspaceIdFromSlug(rootWorkspaceSlug)
  ) {
    return (
      <Navigate
        replace
        to={`/${slug}/${rootWorkspaceSlug}` + otherParams + location?.search}
      />
    );
  }

  return <SlugRedirect />;
};

export const SlugRedirect = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();
  const {
    user,
    slug,
    isLoading: isUserLoading,
    getWorkspaceIdFromSlug,
  } = useUser();
  const ldClient = useLDClient();

  useUpdateSentryTransactionNamesForWorkspaceRoutes();
  // Forward allowlisted query params as tags
  useSentryQueryParamTags();

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (isUserLoading) return;

    // Handle redirects when the root_workspace_slug is not the current workspace slug
    const redirect = async () => {
      // Redirect to top level routes with a workspace slug

      if (slug !== root_workspace_slug) {
        const workspaceId = getWorkspaceIdFromSlug(root_workspace_slug!);
        if (workspaceId) {
          const isSwitching = await switchWorkspace(
            workspaceId,
            `${window.location.pathname}${window.location.search}`,
          );
          if (!isSwitching) {
            // Failed to switch into this workspace slug
            window.location.href = "/";
          }
        } else if (user?.can_impersonate) {
          const isSwitching = await adminSwitchWorkspace(
            root_workspace_slug!,
            `${window.location.pathname}${window.location.search}`,
          );
          if (!isSwitching) {
            // Failed to switch into this workspace slug
            window.location.href = "/";
          }
        } else {
          // Failed to switch into this workspace slug
          window.location.href = "/";
        }
        // Every branch above queues a full-page navigation via
        // window.location.href, which only fires on the next macrotask.
        // Return early so we don't flip isLoading to false and mount the
        // Outlet against the stale (pre-switch) session — that race lets
        // route guards inside the Outlet (e.g. PermissionedRoute with a
        // redirect) read cached permissions for the old workspace and
        // navigate("/") before the browser unloads, dropping the deep-link
        // and dumping the user on the home page.
        return;
      }
      setIsLoading(false);
    };

    redirect();
  }, [root_workspace_slug, slug, isUserLoading]);

  // Flags start empty until the client is ready, and flag-gated routes treat empty as off and redirect.
  return isLoading || !ldClient ? <PageSpinner /> : <Outlet />;
};
